import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, UserCircle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { PatriaWordmark } from "@/components/patria/PatriaWordmark";
import MyPatriaWordmark from "@/components/patria/MyPatriaWordmark";
import api, { fileUrl } from "@/lib/api";
import { useNavItems } from "./Sidebar";
import { NavLink } from "react-router-dom";

export function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const items = useNavItems();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (mounted) setUnread(data.count || 0);
      } catch {
        /* ignore */
      }
    };
    load();
    const iv = setInterval(load, 30000);
    const onUpdated = () => load();
    window.addEventListener("mypatria:notifications-updated", onUpdated);
    return () => {
      mounted = false;
      clearInterval(iv);
      window.removeEventListener("mypatria:notifications-updated", onUpdated);
    };
  }, []);

  const initials =
    user?.name
      ?.split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "";

  return (
    <header
      className="sticky top-0 z-30 bg-cream-100/95 backdrop-blur border-b border-sage-300/70"
      data-testid="app-header"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            className="lg:hidden patria-btn-ghost p-2"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            {settings?.logo_path ? (
              <img
                src={fileUrl(settings.logo_path)}
                alt={settings.org_name || "Patria"}
                className="h-8 w-auto rounded-md"
              />
            ) : (
              <MyPatriaWordmark size="md" />
            )}
            <span className="hidden sm:inline-block h-6 w-px bg-sage-300" aria-hidden />
            <span className="hidden sm:inline text-xs uppercase tracking-widest text-charcoal-muted">
              People Operations
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate("/notifications")}
            className="relative p-2 rounded-full hover:bg-sage-200/70 transition-colors"
            aria-label="Notifications"
            data-testid="notification-bell"
          >
            <Bell className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
            {unread > 0 && (
              <span
                data-testid="notification-unread-count"
                className="absolute -top-0.5 -right-0.5 h-5 min-w-5 rounded-full bg-terracotta-500 text-cream-100 text-[10px] font-semibold flex items-center justify-center px-1"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>

          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-sage-200/70 transition-colors"
              data-testid="user-menu-btn"
            >
              {user?.avatar_path ? (
                <img
                  src={fileUrl(user.avatar_path)}
                  alt={user.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <span className="h-8 w-8 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center text-xs font-semibold">
                  {initials || "P"}
                </span>
              )}
              <span className="hidden sm:inline text-sm font-medium text-charcoal">
                {user?.name?.split(" ")[0]}
              </span>
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 patria-card p-2"
                onMouseLeave={() => setMenuOpen(false)}
                data-testid="user-menu-dropdown"
              >
                <div className="px-3 py-2 border-b border-sage-300/60">
                  <p className="text-sm font-semibold text-forest-900">{user?.name}</p>
                  <p className="text-xs text-charcoal-muted">{user?.email}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-widest text-terracotta-500">
                    {user?.role?.replace("_", " ")}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/profile");
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-sage-200/60 text-sm text-charcoal"
                  data-testid="user-menu-profile"
                >
                  <UserCircle className="h-4 w-4" strokeWidth={1.5} /> My profile
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-sage-200/60 text-sm text-terracotta-600"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-forest-900/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-cream-100 p-5 shadow-card animate-fade-up">
            <div className="flex items-center justify-between mb-6">
              <MyPatriaWordmark size="md" />
              <button
                onClick={() => setDrawerOpen(false)}
                className="patria-btn-ghost p-2"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {items.map(({ to, label, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  data-testid={`d-${testId}`}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${
                      isActive
                        ? "bg-forest-600 text-cream-100"
                        : "text-charcoal hover:bg-sage-200/70"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
